import { useState, useEffect, useRef } from 'react';
import { db } from '../firebase.js';
import { collection, query, orderBy, onSnapshot, where, doc, updateDoc, writeBatch, getDocs } from 'firebase/firestore';
import { buildUserNotificationsPath, ADMIN_NOTIF_COLLECTION, normalizeNotification } from '../lib/notifications.js';

// --- MEJORA: Crear una única instancia de Audio para reutilizar ---
// y un flag para saber si el audio ha sido "desbloqueado" por el usuario.
let audioContextUnlocked = false;
const notificationAudio = new Audio('/sounds/notification.mp3');
notificationAudio.muted = true; // Empezar silenciado

const useNotifications = (userId, isAdmin = false, settings = {}) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [activeCollectionPath, setActiveCollectionPath] = useState(null);
    
    // --- NEW: State to hold just the new notifications for toasting ---
    const [newNotifications, setNewNotifications] = useState([]);

    const knownNotifIds = useRef(new Set()); // Use a ref to track seen IDs
    const isInitialLoadRef = useRef(true);
    const { soundsEnabled = true } = settings;

    useEffect(() => {
        if (!userId && !isAdmin) {
            setNotifications([]);
            setUnreadCount(0);
            setLoading(false);
            setActiveCollectionPath(null);
            return;
        }

        setLoading(true);
        const canFallbackToUser = isAdmin && !!userId;
        let unsubscribe = () => {};

        const attachListener = (collectionPath, allowFallback) => {
            setActiveCollectionPath(collectionPath);
            const q = query(collection(db, collectionPath), orderBy('createdAt', 'desc'));
            unsubscribe = onSnapshot(q, (snapshot) => {
            const notifs = snapshot.docs.map(d => normalizeNotification({ id: d.id, ...d.data() })); // eslint-disable-line
            
            if (isInitialLoadRef.current) {
                // En la carga inicial, solo poblamos los IDs conocidos y no disparamos toasts
                notifs.forEach(n => knownNotifIds.current.add(n.id));
                isInitialLoadRef.current = false;
            } else {
                // En actualizaciones posteriores, encontramos las que son genuinamente nuevas
                const justAdded = notifs.filter(n => !knownNotifIds.current.has(n.id));
                
                if (justAdded.length > 0) {
                    // Este es el nuevo array de notificaciones para mostrar como toast
                    setNewNotifications(justAdded);

                    // Reproducir sonido para el nuevo lote
                    if (soundsEnabled && audioContextUnlocked) {
                        notificationAudio.currentTime = 0;
                        notificationAudio.play().catch((e) => console.warn('Could not play notification sound:', e));
                    }

                    // Actualizar el conjunto de IDs conocidos
                    justAdded.forEach(n => knownNotifIds.current.add(n.id));
                }
            }

                const unread = notifs.filter(n => !n.read).length;
                setNotifications(notifs);
                setUnreadCount(unread);
                setLoading(false);
            }, (error) => {
                if (error.code === 'permission-denied' && allowFallback && userId) {
                    console.warn('Sin permiso para admin_notifications, usando la bandeja del usuario.');
                    unsubscribe();
                    attachListener(buildUserNotificationsPath(userId), false);
                    return;
                }
                console.error(`Error listening to notifications from ${collectionPath}:`, error);
                setLoading(false);
            });
        };

        const initialPath = isAdmin ? ADMIN_NOTIF_COLLECTION : buildUserNotificationsPath(userId);
        attachListener(initialPath, canFallbackToUser);

        return () => unsubscribe();
    }, [userId, isAdmin, soundsEnabled]);

    // --- MEJORA: Desbloquear el audio con la primera interacción del usuario ---
    useEffect(() => {
        const unlockAudio = () => {
            if (!audioContextUnlocked) {
                notificationAudio.muted = false; // Quitar el silencio
                notificationAudio.play().then(() => notificationAudio.pause()).catch(() => {});
                audioContextUnlocked = true;
                // Remover el listener una vez que el audio ha sido desbloqueado.
                window.removeEventListener('click', unlockAudio);
            }
        };

        window.addEventListener('click', unlockAudio);
        return () => window.removeEventListener('click', unlockAudio);
    }, []);

    const resolveCollectionPath = () => {
        if (activeCollectionPath) return activeCollectionPath;
        if (isAdmin) return ADMIN_NOTIF_COLLECTION;
        return userId ? buildUserNotificationsPath(userId) : null;
    };

    const markAsRead = async (notificationId) => {
        const collectionPath = resolveCollectionPath();
        if (!collectionPath || !notificationId) return;
        const notifRef = doc(db, collectionPath, notificationId);
        try {
            await updateDoc(notifRef, { read: true });
        } catch (error) {
            console.error("Error al marcar como leída:", error);
        }
    };

    const markAllAsRead = async () => {
        const collectionPath = resolveCollectionPath();
        if (!collectionPath || unreadCount === 0) return;
        const q = query(collection(db, collectionPath), where('read', '==', false));
        
        try {
            const unreadSnapshot = await getDocs(q);
            if (unreadSnapshot.empty) return;

            const batch = writeBatch(db);
            unreadSnapshot.docs.forEach(doc => {
                batch.update(doc.ref, { read: true });
            });
            await batch.commit();
        } catch (error) {
            console.error("Error marking all notifications as read:", error);
        }
    };

    // --- NUEVO: Devolver las notificaciones nuevas y una función para limpiarlas ---
    const clearNewNotifications = () => setNewNotifications([]);

    return { 
        notifications, 
        unreadCount, 
        loading, 
        markAsRead, 
        markAllAsRead, 
        newNotifications, // <-- Exponer las notificaciones nuevas
        clearNewNotifications, // <-- Exponer una forma de limpiarlas
        activeCollectionPath // <-- Exponer para debugging
    };
};

export default useNotifications;